(() => {
  const mobile = window.matchMedia('(max-width: 749px)');
  if (!mobile.matches) return;

  const storageKey = 'velora-chaty-position-v3';
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const selectors = [
    '.chaty-app-widget',
    '[id^="chaty-app-"][class*="chaty-app-widget"]',
    '[id^="chaty-widget"]',
    '.chaty-widget',
  ];
  let attempts = 0;
  let timer = null;

  const readPosition = () => {
    try {
      return sessionStorage.getItem(storageKey) || 'right';
    } catch (_error) {
      return 'right';
    }
  };

  const savePosition = (position) => {
    try {
      sessionStorage.setItem(storageKey, position);
    } catch (_error) {
      // Dragging still works when browser storage is unavailable.
    }
  };

  const findFixedWidget = () => {
    const candidates = document.querySelectorAll(selectors.join(','));
    for (const candidate of candidates) {
      let element = candidate;
      while (element && element !== document.body) {
        if (getComputedStyle(element).position === 'fixed') return element;
        element = element.parentElement;
      }
    }
    return null;
  };

  const setSide = (widget, side, animate = false) => {
    const previousRect = widget.hidden ? null : widget.getBoundingClientRect();

    if (side === 'hidden') {
      const finishHiding = () => {
        document.body.classList.add('velora-chaty-dismissed');
        widget.hidden = true;
      };

      if (!animate || reducedMotion.matches || !widget.animate) {
        finishHiding();
        return;
      }

      const direction =
        previousRect && previousRect.left + previousRect.width / 2 < window.innerWidth / 2 ? -1 : 1;
      widget
        .animate(
          [
            { transform: 'translate3d(0, 0, 0)', opacity: 1 },
            { transform: `translate3d(${direction * 2.4}rem, 0, 0)`, opacity: 0 },
          ],
          { duration: 300, easing: 'cubic-bezier(.22,.72,.16,1)', fill: 'forwards' }
        )
        .finished.then(finishHiding, finishHiding);
      return;
    }

    document.body.classList.remove('velora-chaty-dismissed');
    widget.hidden = false;

    const trigger = widget.querySelector('.chaty-app-widget-trigger, .chaty-app-channel');
    const visibleWidth = trigger?.getBoundingClientRect().width || 44;
    const leftAnchor = Math.ceil(visibleWidth + 12);

    widget.style.setProperty('left', side === 'left' ? `${leftAnchor}px` : 'auto', 'important');
    widget.style.setProperty('right', side === 'right' ? '12px' : 'auto', 'important');

    if (!animate || reducedMotion.matches || !previousRect || !widget.animate) return;
    const nextRect = widget.getBoundingClientRect();
    const deltaX = previousRect.left - nextRect.left;
    if (Math.abs(deltaX) < 1) return;

    widget.animate(
      [
        { transform: `translate3d(${deltaX}px, 0, 0)`, opacity: 1 },
        { transform: 'translate3d(0, 0, 0)', opacity: 1 },
      ],
      { duration: 350, easing: 'cubic-bezier(.22,.72,.16,1)' }
    );
  };

  const enableDragging = (widget) => {
    if (widget.dataset.veloraChatyDraggable === 'true') return;
    widget.dataset.veloraChatyDraggable = 'true';
    widget.classList.add('velora-chaty-target');

    // Chaty renders this label dynamically. On mobile, keep only the WhatsApp icon.
    const hideChatLabel = () => {
      widget.querySelectorAll('span, p, div, strong, small').forEach((element) => {
        const label = element.textContent.trim().toLowerCase();
        if (
          (label === 'chatea con nosotras' || label === 'chatea con nosotros') &&
          !element.querySelector('img, svg, button')
        ) {
          element.style.setProperty('display', 'none', 'important');
        }
      });
    };
    hideChatLabel();

    // The app may add its label after its initial widget markup is available.
    new MutationObserver(hideChatLabel).observe(widget, { childList: true, subtree: true, characterData: true });

    const storedPosition = readPosition();
    setSide(widget, storedPosition);
    if (storedPosition === 'hidden') return;

    const dismissButton = document.createElement('button');
    dismissButton.type = 'button';
    dismissButton.className = 'velora-chaty-dismiss';
    dismissButton.setAttribute('aria-label', 'Ocultar botón de WhatsApp');
    dismissButton.innerHTML = '<span aria-hidden="true">×</span>';
    widget.appendChild(dismissButton);

    let drag = null;
    let suppressClick = false;

    widget.addEventListener(
      'pointerdown',
      (event) => {
        if (event.target.closest('.velora-chaty-dismiss')) return;
        if (event.pointerType !== 'touch') return;
        const widgetRect = widget.getBoundingClientRect();
        const visibleElement = widget.querySelector('.chaty-app-widget-trigger, .chaty-app-channel') || widget;
        const visibleRect = visibleElement.getBoundingClientRect();
        drag = {
          pointerId: event.pointerId,
          startX: event.clientX,
          startY: event.clientY,
          initialLeft: widgetRect.left,
          width: visibleRect.width || 44,
          startSide: readPosition() === 'left' ? 'left' : 'right',
          deltaX: 0,
          horizontal: false,
        };
        widget.setPointerCapture?.(event.pointerId);
      },
      { passive: true, capture: true }
    );

    widget.addEventListener(
      'pointermove',
      (event) => {
        if (!drag || drag.pointerId !== event.pointerId) return;
        const deltaX = event.clientX - drag.startX;
        const deltaY = event.clientY - drag.startY;
        drag.deltaX = deltaX;

        if (!drag.horizontal && Math.hypot(deltaX, deltaY) > 7) {
          if (Math.abs(deltaX) <= Math.abs(deltaY)) {
            drag = null;
            return;
          }
          drag.horizontal = true;
        }
        if (!drag.horizontal) return;

        widget.classList.add('is-dragging');
        widget.style.setProperty('right', 'auto', 'important');
        widget.style.setProperty('left', `${drag.initialLeft + deltaX}px`, 'important');
        event.preventDefault();
      },
      { passive: false, capture: true }
    );

    const finishDrag = (event) => {
      if (!drag || drag.pointerId !== event.pointerId) return;

      if (drag.horizontal) {
        const visibleElement = widget.querySelector('.chaty-app-widget-trigger, .chaty-app-channel') || widget;
        const rect = visibleElement.getBoundingClientRect();
        const outsideLeft = rect.right < drag.width * 0.45;
        const outsideRight = rect.left > window.innerWidth - drag.width * 0.45;

        if (outsideLeft || outsideRight) {
          savePosition('hidden');
          setSide(widget, 'hidden', true);
        } else {
          let side = drag.startSide;
          if (drag.deltaX <= -24) side = 'left';
          if (drag.deltaX >= 24) side = 'right';
          savePosition(side);
          setSide(widget, side, true);
        }
        suppressClick = true;
      }

      widget.classList.remove('is-dragging');
      drag = null;
    };

    widget.addEventListener('pointerup', finishDrag, true);
    widget.addEventListener('pointercancel', finishDrag, true);
    dismissButton.addEventListener('pointerdown', (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      event.stopPropagation();
    }, true);
    let isDismissing = false;
    const dismissWidget = (event) => {
      event.preventDefault();
      event.stopImmediatePropagation();
      if (isDismissing) return;
      isDismissing = true;
      savePosition('hidden');
      setSide(widget, 'hidden', true);
    };
    // A pointer event is more reliable than click here: Chaty also handles touch gestures.
    dismissButton.addEventListener('pointerup', dismissWidget, true);
    dismissButton.addEventListener('click', dismissWidget, true);
    widget.addEventListener(
      'click',
      (event) => {
        if (!suppressClick) return;
        suppressClick = false;
        event.preventDefault();
        event.stopImmediatePropagation();
      },
      true
    );
  };

  const locateWidget = () => {
    attempts += 1;
    const widget = findFixedWidget();
    if (widget) {
      clearInterval(timer);
      enableDragging(widget);
    } else if (attempts >= 24) {
      clearInterval(timer);
    }
  };

  const start = () => {
    document.body.classList.toggle('velora-chaty-dismissed', readPosition() === 'hidden');
    locateWidget();
    if (!document.querySelector('[data-velora-chaty-draggable="true"]')) {
      timer = setInterval(locateWidget, 500);
    }
  };

  start();
})();
